import {
  HoldingPage,
  PageLoadingIndicator,
} from "@ecologi/react-components/src";
import {
  FeedbackForm,
  FeedbackFormVariant,
} from "@ecologi/react-components/src/FeedbackForm";
import { PlatformFeature, ProjectInsights } from "@ecologi/shared";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { Head } from "../../../components";
import {
  APIError,
  forkResult,
  getProjectInsights,
  withPrivatePageGuard,
} from "../../../lib";
import { createGeneralFeedback } from "../../../lib/api/general-feedback";
import { withFeatureFlagGuard } from "../../../lib/auth/with-feature-flag-guard";
import { useStore } from "../../../store";
import { projectTransparencyData } from "./project-transparency-data";
import {
  ProjectInsightsBody,
  ProjectInsightsSectionsProps,
} from "./project-insights";

export const ProjectInsightsPageComponent = () => {
  const { asPath } = useRouter();
  const { user } = useStore();

  const query = useQuery<ProjectInsights.Payload, APIError>(
    ["project-insights", user.userId],
    getProjectInsights,
    {
      retry: 3,
    }
  );

  const derivedProps = useMemo(() => {
    const { data } = query;
    if (!data) return;

    const projectLocationsMap: ProjectInsightsSectionsProps["projectLocationsMap"] =
      {
        locations: data.projectLocations.map((project) => ({
          latitude: project.location.latLong?.[0] || 0,
          longitude: project.location.latLong?.[1] || 0,
          locationName: project.location.name,
          project: {
            title: project.title,
            description: project.snippets.short,
            url: `/projects/${project.slug}`,
          },
        })),
      };

    const supportedProjects: ProjectInsightsSectionsProps["supportedProjects"] =
      {
        projects: data.supportedProjects.map((project) => ({
          title: project.title,
          imageUrl: project.images[0]?.url,
          projectLink: `/projects/${project.slug}`,
          certifications: project.certifications,
          description: project.snippets.short,
          size: project.projectSize,
          carbonTotal: project.totalCo2Avoided,
          variant: project.projectType,
        })),
      };

    const projectUpdates: ProjectInsightsSectionsProps["projectUpdates"] = (
      data.latestProjectUpdates || []
    ).map((update) => ({
      title: update.title,
      date: new Date(update.date),
      description: update.description,
      // de-scoped to link direct to project rather than update itself
      projectUrl: `/projects/${update.project.slug}`,
      projectLocation: update.project?.location.name,
      image: {
        src: update.image.src,
        alt: update.image.alt,
      },
    }));

    const projectTransparency = projectTransparencyData;

    return {
      hasSupportedProjects: supportedProjects.projects.length > 0,
      projectUpdates,
      projectLocationsMap,
      projectTransparency,
      supportedProjects,
    };
  }, [query.data]);

  return forkResult(
    {
      loading: () => (
        <PageLoadingIndicator text="Loading your project insights..." />
      ),
      data: () =>
        derivedProps ? (
          <>
            <Head title="Project insights" />
            <ProjectInsightsBody {...derivedProps} />
            <FeedbackForm
              onSubmit={createGeneralFeedback}
              currentUser={user}
              currentPath={asPath}
              variant={FeedbackFormVariant.SNOWDROP}
            />
          </>
        ) : (
          <PageLoadingIndicator text="Loading your project insights..." />
        ),
    },
    query
  );
};

export const ProjectInsightsPage = withFeatureFlagGuard(
  withPrivatePageGuard(ProjectInsightsPageComponent),
  () => <HoldingPage />,
  { [PlatformFeature.Maintenance]: false }
);

ProjectInsightsPage.layoutProps = {
  isProfile: true,
};
